import React from 'react';
import DocumentTitle from 'react-document-title';
import ImageTrack from '../components/ImageTrack';
import Lightbox from '../components/Lightbox';
import Link from '../components/Link';
import Modal from '../components/Modal';
import H5Embed from '../components/H5Embed';
import UpNextContentSection from '../components/UpNextContentSection';
import ListingPreviewsGroup from '../components/ListingPreviewsGroup';
import { logOrThrowError } from '../utility/logging';
import './Listing.scss';

import icon0 from '../images/icon-arrow-med.png';
import icon1 from '../images/icon-flpln.png';
import saveIcon from '../images/icon-save.png';
import savedIcon from '../images/icon-save_c.png';
import virtualTourIcon from '../images/icon-tour.png';

export default class ListingPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showCopyLinkModal: false,
      initialLightboxSlide: -1,
    };

    this.openSharePopup = this.openSharePopup.bind(this);
  }

  openSharePopup(siteName, url) {
    let message = 'Check out this listing';
    let popupUrl = (() => {
      switch (siteName) {
        case 'facebook':
          return `https://www.facebook.com/sharer.php?u=${encodeURIComponent(
            url
          )}`;

        case 'twitter':
          return `https://twitter.com/intent/tweet?url=${encodeURIComponent(
            url
          )}&text=${encodeURIComponent(message)}`;

        default:
          return undefined;
      }
    })();

    return function (e) {
      if (!popupUrl) {
        logOrThrowError(
          new Error(`Could not compute popupUrl for ${siteName}`)
        );
      }

      typeof window !== 'undefined' &&
        window.open(popupUrl, 'SharePopup', 'width=600,height=400'); // eslint-disable-line no-undef
      e.preventDefault();
    };
  }

  render() {
    const pageUrl =
      this.props.baseUrl +
      this.props.location.pathname +
      this.props.location.search;
    const listing = this.props.listing;
    const listingLoading = listing === undefined;
    const listingError = listing instanceof Error;
    const grossPriceDisclaimer = getGrossPriceDisclaimer(
      listing && listing.description
    );

    if (listingLoading) {
      return (
        <div className="loading-or-error">
          <p>Loading...</p>
        </div>
      );
    }

    if (listingError) {
      return (
        <div className="loading-or-error">
          <p>An error ocurrred loading this listing.</p>
          <p>
            The listing may no longer be available. Alternatively, refreshing
            the page may resolve the issue.
          </p>
          <p>
            You can also view the{' '}
            <Link to="/availability">other availabilities.</Link>
          </p>
        </div>
      );
    }

    const amenities = listing.amenities || [];
    const amenitiesSplitIndex = Math.floor(amenities.length / 2);
    const amenitiesPart1 = amenities.slice(0, amenitiesSplitIndex);
    const amenitiesPart2 = amenities.slice(amenitiesSplitIndex);
    const listAmenity = (it, index) => <li key={index}>{it}</li>;

    const usableNestioImages = listing.photos
      .map((it) => ({ src: it.original || it.xlarge || it.large }))
      .filter((it) => it.src !== undefined);

    const heroImageStyles = {
      backgroundImage: 'url(' + usableNestioImages[0].src + ')',
    };

    // Create the image track images by using the usable nestio images, but
    // using the first photo as the poster/preview image for the for the
    // virtual tour in the image track, since we don't have one from h5.
    // We'll add filters and an icon to it to make it look more like the tour
    // videos. This is a bit awkward UX wise, but apparently it's fine.
    const useCarouselAndLightbox = usableNestioImages.length > 1;
    const hasH5Tour = Boolean(listing.h5Url);
    const hasH5PosterImage = hasH5Tour && useCarouselAndLightbox;

    const h5tourLightboxSlide = hasH5Tour && {
      custom: <H5Embed url={listing.h5Url} controlless />,
    };

    const h5PosterImage = hasH5PosterImage && {
      ...usableNestioImages[0],
      variants: ['h5-poster'],
    };

    const imageTrackImages = [
      ...(hasH5PosterImage ? [h5PosterImage] : []),
      ...usableNestioImages.slice(hasH5PosterImage ? 1 : 0),
    ];

    // Note that, when we have the first photo open the virtual tour, that gets
    // confusing, bc then how does the user see the first photo in the lightbox?
    // Our answer: tack the first photo on at the end.
    const lightboxSlides = [
      ...(h5tourLightboxSlide ? [h5tourLightboxSlide] : []),
      ...[
        ...usableNestioImages.slice(hasH5PosterImage ? 1 : 0),
        ...(hasH5PosterImage ? [usableNestioImages[0]] : []),
      ].map((it) => ({ img: it })),
    ];

    const saveLi = (
      <li>
        <a
          tabIndex="0"
          onClick={(e) => {
            e.preventDefault();
            this.props.toggleSaved();
          }}
          onKeyPress={(e) => {
            e.preventDefault();
            this.props.toggleSaved();
          }}
        >
          <img src={listing.isSaved ? savedIcon : saveIcon} className="icon" />
          {listing.isSaved ? 'Saved' : 'Save'}
        </a>
      </li>
    );

    return (
      <DocumentTitle title={this.props.pageTitle}>
        <div className="content" id="main" role="main">
          <Modal
            id="copyLinkPop"
            isOpened={this.state.showCopyLinkModal}
            onClose={() => {
              this.setState({ showCopyLinkModal: false });
              document
                .querySelector('.global-layout')
                .classList.remove('global-layout--fixed');
            }}
            persistent={false}
          >
            <h2>Share this listing</h2>
            <p>Copy the listing URL below to share.</p>
            <textarea value={pageUrl} readOnly></textarea>
          </Modal>

          <div className="back">
            <img src={icon0} className="icon" />
            <Link tabIndex="0" to="/availability" activeClassName="active">
              Back to Availabilities
            </Link>
          </div>

          {useCarouselAndLightbox ? (
            <ImageTrack
              images={imageTrackImages}
              buttonText="View Photos"
              tabIndex="0"
              onCtaClick={() => {
                this.setState({ initialLightboxSlide: 0 });
              }}
              onImgClick={(index) => {
                this.setState({ initialLightboxSlide: index });
              }}
            />
          ) : (
            <div className="listing-single-image" style={heroImageStyles}></div>
          )}

          {useCarouselAndLightbox && (
            <Lightbox
              slides={lightboxSlides}
              startSlide={this.state.initialLightboxSlide}
              onClose={() => this.setState({ initialLightboxSlide: -1 })}
            />
          )}

          <div className="aptinfo">
            <div className="aptinfoblock">
              <h2 className="h5">300 ashland</h2>
              <h1>Apartment #{listing.unitNumber}</h1>
              <h2
                className="h5"
                dangerouslySetInnerHTML={listingSpecsHtml(listing)}
              ></h2>
              <h2 className="cost h5">
                ${listing.price}/month{listing.showAsterisk}
              </h2>
              <p>All units are no fee</p>
              {/* {grossPriceDisclaimer &&
                <small>{grossPriceDisclaimer}</small>} */}
              <p>
                {listing.description.split('.').slice(0, 3).join('. ') + '.'}
              </p>
            </div>
            <div className="aptinfoblock">
              <ul>
                <li>
                  <h2 className="h5">Features</h2>
                </li>
                <ul className="col">{amenitiesPart1.map(listAmenity)}</ul>
                <ul className="col">{amenitiesPart2.map(listAmenity)}</ul>
              </ul>
            </div>

            <div className="aptinfoblock">
              <ul>
                <li>
                  <a
                    target="_blank"
                    className="button button--primary"
                    href="https://www.on-site.com/apply/property/201823"
                  >
                    Apply Now
                  </a>
                </li>
                <li>
                  <a tabIndex="0" href={listing.floorplanUrl} target="_blank">
                    <img src={icon1} className="icon" /> Floorplan
                  </a>
                </li>
                <li>
                  <a
                    tabIndex="0"
                    className="button"
                    onClick={(e) => {
                      e.preventDefault();
                      this.props.openContactModal();
                    }}
                    onKeyPress={(e) => {
                      e.preventDefault();
                      this.props.openContactModal();
                    }}
                  >
                    Contact
                  </a>
                </li>
                {hasH5PosterImage ? (
                  <li>
                    <a
                      tabIndex="0"
                      onClick={(e) => {
                        e.preventDefault();
                        this.setState({ initialLightboxSlide: 0 });
                      }}
                      onKeyPress={(e) => {
                        e.preventDefault();
                        this.setState({ initialLightboxSlide: 0 });
                      }}
                    >
                      <img src={virtualTourIcon} className="icon" /> Virtual
                      Tour
                    </a>
                  </li>
                ) : (
                  saveLi
                )}
                <li className="rental-fees">
                  <p className="heading">Required Fees To Rent:</p>
                  <p>$20 Application Fee per applicant</p>
                  <p>1st Month’s Rent</p>
                  <p>1 Month Security Deposit</p>
                </li>
                <p className="disclaimer">
                  All pricing and availability are subject to change at any
                  time. All Two Trees apartments are No Fee when rented directly
                  from Landlord without the use of a broker unless otherwise
                  specified. Listings and advertisements on this site are for
                  informational purposes only and do not create a relationship
                  between a real estate licensee or broker and the landlord or
                  managing agent. Listings and ads may not be republished.
                </p>

                <li>
                  <ul className="share-links">
                    <li className="icon-fb button">
                      <a
                        tabIndex="0"
                        onClick={this.openSharePopup('facebook', pageUrl)}
                        onKeyPress={this.openSharePopup('facebook', pageUrl)}
                      >
                        Facebook
                      </a>
                    </li>
                    <li className="icon-twitter button">
                      <a
                        tabIndex="0"
                        onClick={this.openSharePopup('twitter', pageUrl)}
                        onKeyPress={this.openSharePopup('twitter', pageUrl)}
                      >
                        Twitter
                      </a>
                    </li>
                    <li className="icon-email button">
                      <a
                        tabIndex="0"
                        href={`mailto:?body=${encodeURIComponent(
                          'Check out this listing: ' + pageUrl
                        )}`}
                        target="_blank"
                      >
                        Email
                      </a>
                    </li>
                    <li className="icon-link button">
                      <a
                        tabIndex="0"
                        onClick={(e) => {
                          e.preventDefault();
                          this.setState({ showCopyLinkModal: true });
                          document
                            .querySelector('.global-layout')
                            .classList.add('global-layout--fixed');
                        }}
                        onKeyPress={(e) => {
                          e.preventDefault();
                          this.setState({ showCopyLinkModal: true });
                          document
                            .querySelector('.global-layout')
                            .classList.add('global-layout--fixed');
                        }}
                      >
                        Copy Link
                      </a>
                    </li>
                  </ul>
                </li>
                {hasH5PosterImage && saveLi}
              </ul>
            </div>
          </div>
          {this.props.similarListings.length > 0 && (
            <UpNextContentSection className="similar">
              <div className="itemHolder">
                <h2 className="h5">Similar Apartments</h2>
                <ListingPreviewsGroup items={this.props.similarListings} />
              </div>
            </UpNextContentSection>
          )}
        </div>
      </DocumentTitle>
    );
  }
}

/**
 * Parses the description from nestio to extract the caveat about
 * the gross price.
 */
function getGrossPriceDisclaimer(descriptionString) {
  if (!descriptionString) return descriptionString;

  // sometimes the gross and net rent are mentioned in the same sentence;
  // sometimes in separate sentences. Sometimes, if they're mentioned in one
  // sentence, it's the first, and sometimes it's the second. Etc. And
  // sometimes there's extra content mentioned around them in asterisks, but
  // with no period to break that content into an separate sentence. So we
  // split the string at asterisks and periods, and then keep just the relevant
  // parts, which we treat as separate sentences.
  const finalSentencesString = (
    descriptionString
      .split(/\.|\*|\n/)
      .filter((sentence) => /net|gross/i.test(sentence))
      .join('.') + '.'
  ).trim();

  if (finalSentencesString == '.') {
    return undefined;
  }

  return (
    '* ' +
    finalSentencesString.charAt(0).toUpperCase() +
    finalSentencesString.slice(1)
  );
}

function listingSpecsHtml(listing) {
  const listingInfo = [
    { info: listing.bedroomCount, unit: 'Bed' },
    { info: listing.bathroomCount, unit: 'Bath' },
    { info: listing.sqft, unit: 'sqft' },
  ];

  // if bed, bath, or sqft isn't provided, don't show it.
  return {
    __html: listingInfo
      .filter((it) => it.info != undefined)
      .map((it) => it.info + ' ' + it.unit)
      .join('&nbsp;&nbsp;&middot;&nbsp;&nbsp'),
  };
}



// WEBPACK FOOTER //
// ./client/pages/Listing.js